import React, { useState, useEffect } from 'react';
import { LocateIcon } from './icons';
import { locationHelp, blockedByPolicy } from '../lib/platform';

/**
 * Webbläsarens platsåtkomst — det som styr om appen alls får läsa var du är.
 *
 * Den är skild från delningsreglagen: åtkomsten avgör om appen vet din
 * position, reglagen avgör vem som får se den. Utan åtkomst har reglagen
 * ingen verkan, och då ska det synas i stället för att tyst inte fungera.
 *
 * Tillståndet läses via Permissions API när det finns. Saknas det (äldre
 * webbläsare) visas knappen ändå — då får försöket ge svaret.
 */
export default function LocationAccess({ onGranted }) {
  const [state, setState] = useState('okänt'); // 'beviljad' | 'nekad' | 'ofrågad' | 'okänt'
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!navigator.permissions || !navigator.permissions.query) return undefined;

    let cancelled = false;
    let status = null;
    const translate = (value) => (
      { granted: 'beviljad', denied: 'nekad', prompt: 'ofrågad' }[value] || 'okänt'
    );

    navigator.permissions.query({ name: 'geolocation' })
      .then((result) => {
        // Panelen kan ha stängts innan svaret kom.
        if (cancelled) return;
        status = result;
        setState(translate(result.state));
        // Ändras tillståndet i webbläsarens inställningar ska vyn följa med.
        result.onchange = () => setState(translate(result.state));
      })
      .catch(() => { if (!cancelled) setState('okänt'); });

    return () => {
      cancelled = true;
      if (status) status.onchange = null;
    };
  }, []);

  /**
   * Ber webblasaren om tillstand.
   *
   * Rensar ocksa markeringen om att fragan redan stallts pa den har
   * enheten — annars kan den som en gang tryckt "Inte nu" aldrig fa fram
   * fragan igen, och appen ser ut att strunta i en.
   */
  const request = () => {
    try { localStorage.removeItem('locationPromptDismissed'); } catch { /* privat läge */ }

    if (!navigator.geolocation) {
      setState('nekad');
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      () => {
        setState('beviljad');
        setBusy(false);
        if (onGranted) onGranted();
      },
      () => {
        setState('nekad');
        setBusy(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Ordagrant vad webblasaren svarar, sa att det gar att felsoka utan att
  // gissa — och att beratta for nagon annan vad som star.
  const raw = (
    <p className="access-card__state">
      Webbläsaren säger: <strong>{state}</strong>
    </p>
  );

  if (state === 'beviljad') {
    return (
      <div className="access-card access-card--ok">
        <span className="access-card__icon"><LocateIcon size={20} /></span>
        <div className="grow">
          <p className="access-card__title">Platsåtkomst är på</p>
          <p className="access-card__help">
            Appen kan se var du är. Vem som ser det bestämmer du nedan.
          </p>
          {raw}
        </div>
      </div>
    );
  }

  if (state === 'nekad' && blockedByPolicy()) {
    return (
      <div className="access-card access-card--warn">
        <span className="access-card__icon access-card__icon--warn"><LocateIcon size={20} /></span>
        <div className="grow">
          <p className="access-card__title">Servern förbjuder platsåtkomst</p>
          <p className="access-card__help">
            Sidans svar innehåller en Permissions-Policy med <code>geolocation=()</code>,
            som stänger av platsen för alla — även sidan själv. Ingenting du ställer in
            på telefonen eller i webbläsaren kan ändra det. Den som sköter servern
            (HAProxy) behöver byta till <code>geolocation=(self)</code>.
          </p>
          {raw}
        </div>
      </div>
    );
  }

  if (state === 'nekad') {
    const help = locationHelp(false);
    return (
      <div className="access-card access-card--warn">
        <span className="access-card__icon access-card__icon--warn"><LocateIcon size={20} /></span>
        <div className="grow">
          <p className="access-card__title">{help.title}</p>
          <p className="access-card__help">
            Appen vet inte var du är, så inställningarna nedan har ingen verkan.
          </p>
          <ol className="access-card__steps">
            {help.steps.map(step => <li key={step}>{step}</li>)}
          </ol>
          <button className="btn btn--sm btn--outline" onClick={request} disabled={busy}>
            {busy ? 'Väntar…' : 'Fråga igen'}
          </button>
          {raw}
        </div>
      </div>
    );
  }

  return (
    <div className="access-card">
      <span className="access-card__icon"><LocateIcon size={20} /></span>
      <div className="grow">
        <p className="access-card__title">Platsåtkomst</p>
        <p className="access-card__help">
          Appen behöver få läsa din position för att kunna visa dig på kartan
          och räkna ut rutter från där du står. Frågan måste komma från en
          knapptryckning — annars tystar vissa webbläsare den.
        </p>
        <button className="btn btn--primary btn--sm" onClick={request} disabled={busy}>
          {busy ? 'Väntar…' : 'Tillåt platsåtkomst'}
        </button>
        {raw}
      </div>
    </div>
  );
}
